import { useEffect, useRef, useState } from "react";
import {
	Flex,
	Icon,
	Img,
	MenuButton,
	type MenuButtonProps,
	MenuItem,
	MenuList,
	type MenuListProps,
	Text,
	useDisclosure,
	useMultiStyleConfig
} from "@chakra-ui/react";
import { FiChevronDown } from "react-icons/fi";
import { GET } from "@/services/api/client";
import { envVar } from "@/services/env";
import { useHybridRouter } from "@/services/router/useHybridRouter";
import type { ShareClassDashboard } from "@/services/server/v1/getShareClassDashboard";
import { htmlify } from "@/services/utils";
import type { FundDetailPagePathProps } from "@/types/FundDetailPagePathProps";
import type { ShareClassDocument } from "@/types/ShareClassDocument";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";
import type { GetDocumentsEndpoint } from "@/types/endpoints/GetDocumentsEndpoint";
import { AccessibleMenu } from "../AccessibleMenu/AccessibleMenu";
import { AppLink } from "../AppLink/AppLink";

interface DocumentDownloadDropdownProps {
	translations: ShareClassTranslationMap;
	shareClass: ShareClassDashboard["shareClass"];
	externalPayload: Record<string, unknown>;
}

export const DocumentDownloadDropdown = ({ shareClass, externalPayload, translations }: DocumentDownloadDropdownProps) => {
	const styleParts = useMultiStyleConfig("DocumentDownloadDropdown");
	const {
		query: { externalId, location, channel, language }
	} = useHybridRouter<FundDetailPagePathProps>();

	const { isOpen, onClose, onOpen } = useDisclosure();
	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const SCROLL_DELAY = 30;

	const [documents, setDocuments] = useState<ShareClassDocument[]>();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const callGetDocuments = async () => {
			try {
				const response = await GET<GetDocumentsEndpoint>(`${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getDocuments`, {
					location,
					channel,
					language,
					...(shareClass as any),
					...externalPayload
				});

				setLoading(false);

				if (response.isErr) {
					setDocuments(undefined);
					return;
				}

				const { documents } = response.body;
				const newDocuments = documents && documents.length > 0 ? documents : undefined;

				setDocuments(newDocuments);
			} catch (e) {
				setDocuments(undefined);
				setLoading(false);
			}
		};

		callGetDocuments();
	}, [externalPayload, location, channel, language, shareClass]);

	// To close the menu when scrolling
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleScroll = () => {
			// Clear the existing timeout first
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}

			// Set a new timeout to close the menu after the delay
			scrollTimeoutRef.current = setTimeout(() => {
				onClose();
				scrollTimeoutRef.current = null;
			}, SCROLL_DELAY);
		};

		window.addEventListener("scroll", handleScroll, { passive: true });

		// Cleanup the event listener and any pending timeouts on unmount or when isOpen changes
		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
		};
	}, [isOpen, onClose]);

	if (!documents) {
		return null;
	}

	return (
		<AccessibleMenu id="document-download" isOpen={isOpen} onClose={onClose}>
			<MenuButton
				{...(styleParts.menuButton as MenuButtonProps)}
				data-testid="DocumentDownloadDropdown"
				onClick={isOpen ? onClose : onOpen}
			>
				<Flex alignItems="center" gap={2} color="shade.100">
					<Text as="span">{htmlify(translations.keyDocuments)}</Text> {`(${documents.length})`}
					<Icon boxSize="1.25em" as={FiChevronDown} />
				</Flex>
			</MenuButton>
			<MenuList {...(styleParts.menuList as MenuListProps)}>
				{documents.map(({ url, title, language }, idx) => {
					const titleWithLanguage = `${title} (${language})`;

					return (
						<MenuItem key={idx}>
							<AppLink
								href={url}
								onClick={() => {
									return null;
								}}
								recordNavigation={false}
								target="_blank"
								rel="noreferrer"
								width="full"
							>
								<Flex justifyContent="space-between" width="full">
									<Text textDecoration="none">{htmlify(titleWithLanguage)}</Text>
									<Img
										src={`${envVar("PUBLIC_ENV__APP_HOST")}/img/document.png`}
										width="19px"
										height="24px"
										alt="document image"
									/>
								</Flex>
							</AppLink>
						</MenuItem>
					);
				})}
			</MenuList>
		</AccessibleMenu>
	);
};
